/**
 * Dev-only perf HUD for the 3D fuel layer (issue #276, phase 0). Mounted by
 * FuelLoad3DTrees when `?perf3d` (or localStorage "fisci.perf3d") is set.
 * Shows frame-time p50/p95 (rAF-sampled), the last rebuild's stage timings,
 * instance counts / draw calls / triangles, and JS heap where available.
 */

import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { getPerfSnapshot, subscribePerf } from "./perf";

const hudStyle: React.CSSProperties = {
  position: "fixed",
  top: 8,
  right: 8,
  zIndex: 10000,
  background: "rgba(0, 0, 0, 0.75)",
  color: "#9f9",
  font: "11px/1.5 monospace",
  padding: "6px 10px",
  borderRadius: 4,
  pointerEvents: "none",
  whiteSpace: "pre",
  maxWidth: 340,
};

function percentile(sorted: number[], p: number): number {
  if (sorted.length === 0) return 0;
  return sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * p))];
}

const PerfHud: React.FC = () => {
  const stats = useSyncExternalStore(subscribePerf, getPerfSnapshot);
  const frames = useRef<number[]>([]);
  const [frameStats, setFrameStats] = useState({ p50: 0, p95: 0 });

  // rAF frame-time sampler — p50/p95 over a rolling ~2 s window.
  useEffect(() => {
    let raf = 0;
    let last = performance.now();
    let lastPublish = 0;
    const tick = (now: number) => {
      const dt = now - last;
      last = now;
      const buf = frames.current;
      buf.push(dt);
      if (buf.length > 120) buf.shift();
      if (now - lastPublish > 500) {
        lastPublish = now;
        const sorted = [...buf].sort((a, b) => a - b);
        setFrameStats({
          p50: percentile(sorted, 0.5),
          p95: percentile(sorted, 0.95),
        });
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const heap = (
    performance as unknown as { memory?: { usedJSHeapSize: number } }
  ).memory?.usedJSHeapSize;

  const lines: string[] = [
    `frame  p50 ${frameStats.p50.toFixed(1)}ms  p95 ${frameStats.p95.toFixed(1)}ms`,
  ];
  const t = stats.timings;
  const order = [
    "rebuild.total",
    "rebuild.prep",
    "rebuild.features",
    "rebuild.classify",
    "rebuild.flatten",
    "rebuild.elevGrid",
    "rebuild.fireGrid",
    "rebuild.plant",
    "rebuild.bake",
  ];
  for (const k of order) {
    if (t[k] !== undefined) lines.push(`${k.padEnd(18)} ${t[k].toFixed(1)}ms`);
  }
  const g = stats.gauges;
  if (g.trees !== undefined) {
    lines.push(`trees ${g.trees}  ground ${g.ground ?? 0}`);
  }
  if (g.drawCalls !== undefined) {
    lines.push(`draws ${g.drawCalls}  tris ${((g.triangles ?? 0) / 1e6).toFixed(2)}M`);
  }
  if (g.geometries !== undefined) {
    lines.push(`geoms ${g.geometries}  textures ${g.textures ?? 0}`);
  }
  if (heap !== undefined) {
    lines.push(`heap ${(heap / 1048576).toFixed(0)}MB`);
  }

  return createPortal(<div style={hudStyle}>{lines.join("\n")}</div>, document.body);
};

export default PerfHud;
